import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { GeneralService } from '../../services/general/general.service';
import {
  CalendarOptions,
  EventApi,
  EventSourceInput,
  FullCalendarComponent,
} from '@fullcalendar/angular'; // useful for typechecking
import esLocale from '@fullcalendar/core/locales/es';
import enLocale from '@fullcalendar/core/locales/en-gb';
import ruLocale from '@fullcalendar/core/locales/ru';
import moment from 'moment-timezone';
import { ChangeDetectorRef } from '@angular/core';
import timezones from '../../../assets/data/timezones/index';
import { FormControl } from '@angular/forms';
import { DateRange } from '../../interfaces/date-range.interface';
import { DateTime, Interval } from 'luxon';

@Component({
  selector: 'app-booking-calendar',
  templateUrl: './booking-calendar.component.html',
  styleUrls: ['./booking-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BookingCalendarComponent implements OnInit, OnDestroy {
  _schedule = new Array();
  @ViewChild('calendar') calendarComponent: FullCalendarComponent | undefined;
  @Output() viewRange = new EventEmitter<DateRange>();
  @Output() onEventClicked = new EventEmitter<EventApi>();

  get schedule() {
    return this._schedule;
  }
  @Input() set schedule(schedule: Array<any>) {
    const res = new Array();
    schedule.forEach((event) => {
      if (!event.available) return;

      let className = 'available';
      if (event.selected) className = 'selected';

      event = {
        start: DateTime.fromISO(event.start)
          .setZone(this.timezoneCtrl.value)
          .toString(),
        end: DateTime.fromISO(event.end)
          .setZone(this.timezoneCtrl.value)
          .toString(),

        title: '',
        display: 'block',
        className,
      };
      res.push(event);
    });

    this._schedule = res;
    this.updateCalendarEvents();
  }
  @Input() loading = false;
  public calendarOptions: CalendarOptions = {};
  public isMobile: any;
  public calendarTitle = '';
  public disablePrevButton = false;
  public disableNextButton = false;
  public timezones = new Array();
  public timezoneCtrl = new FormControl();
  public subscriptions = new Array();

  constructor(
    public general: GeneralService,
    private cdRef: ChangeDetectorRef
  ) {
    this.isMobile = this.general.isMobile();
    this.initTimeZones();
    this.configureCalendar();
    this.initSubscriptions();
  }

  async ngOnInit() {}

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  initSubscriptions() {
    this.subscriptions = [
      this.timezoneCtrl.valueChanges.pipe().subscribe((data) => {
        this.setTimeZone(data);
      }),
    ];
  }

  configureCalendar() {
    this.calendarOptions = {
      initialView: 'timeGridWeek',
      allDaySlot: false,
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: false,
        hour12: false,
      },
      eventMinHeight: 30,
      dayHeaderFormat: {
        weekday: this.isMobile ? undefined : 'short',
        month: this.isMobile ? undefined : '2-digit',
        day: '2-digit',
        omitCommas: true,
      },
      displayEventTime: false,
      locales: [esLocale, enLocale, ruLocale],
      locale:
        this.general.translate.currentLang === 'en'
          ? 'en-gb'
          : this.general.translate.currentLang,
      firstDay: 1,
      editable: false,
      events: this.schedule,
      // eventClick: this.handleEventClick.bind(this),
      eventLongPressDelay: 300,
      eventOverlap: false,
      headerToolbar: false,
      datesSet: this.handleDateSet.bind(this),
      titleFormat: { year: 'numeric', month: 'short', day: 'numeric' },
      timeZone: this.timezoneCtrl.value,
      eventClick: this.handleEventClick.bind(this),
    };
  }

  handleDateSet(event: any) {
    this.calendarTitle = event.view.title;
    this.checkDisablePrevButton(event);
    this.checkDisableNextButton(event);

    this.viewRange.emit({
      start: event.startStr,
      end: event.endStr,
      timezone: event.timeZone,
    });
  }

  checkDisablePrevButton(event: any) {
    const today = new Date();

    const isInside = Interval.fromDateTimes(event.start, event.end).contains(
      DateTime.fromJSDate(today)
    );

    this.disablePrevButton = isInside;
  }

  checkDisableNextButton(event: any) {
    console.log(event);
    const now = DateTime.local().toMillis();
    const end = DateTime.fromJSDate(event.end).toMillis();
    this.disableNextButton = end - now >= 1000 * 60 * 60 * 24 * 30;
  }

  calendarNext() {
    const calendar = this.calendarComponent?.getApi();
    calendar?.next();
  }

  calendarPrev() {
    const calendar = this.calendarComponent?.getApi();
    calendar?.prev();
  }

  initTimeZones() {
    this.timezones = timezones.map((timezone) => {
      return { label: timezone.label, value: timezone.tzCode };
    });

    const timezone = this.general.getCurrentTimezone();

    this.timezoneCtrl.patchValue(timezone);
  }

  setTimeZone(timeZone: string) {
    const calendar = this.calendarComponent?.getApi();
    calendar?.setOption('timeZone', timeZone);
    calendar?.refetchEvents();
  }

  updateCalendarEvents() {
    const calendar = this.calendarComponent?.getApi();
    this.calendarOptions.events = this.schedule;
    calendar?.refetchEvents();
  }

  handleEventClick(event: any) {
    if (
      event.event.classNames.includes('available') ||
      event.event.classNames.includes('selected')
    ) {
      this.onEventClicked.emit(event.event);
    }
  }
}
